import {
    MOCK_LATENCY_MS, instanceAt, instanceDto, mockRequest, nextId, notFound, openInstance, planOf, refused,
    upcomingRows, withMockWorld,
} from '../../upcoming/api/mockBackend.js';
import { DAY, iso } from '../../upcoming/api/mockSeed.js';
import { cleanColor, flywheelIconKey } from '../model/flywheelStyle.js';
import {
    HORIZON_DAYS, cleanOverride, firstDates, lengthMs, readiness, selfPublish, shipBlocker,
} from '../model/placements.js';

const SEED_VERSION = 1;
const SHIP_AHEAD = 5;
const KINDS = ['BEAT', 'OBJECTIVE', 'MILESTONE', 'BLOCK'];
const SYSTEM_USER = 'system:live-events';
const NAME_MAX = 80;
const DESCRIPTION_MAX = 500;
const TITLE_MAX = 120;
const NOTE_MAX = 2000;
const MAX_DURATION_S = 366 * 86400;

const DEFAULT_FLYWHEELS = [
    {
        name: 'Core Loop',
        icon: 'repeat',
        color: '#6366f1',
        description: 'The everyday loop — the recurring cadence of events, updates and tuning that keeps players engaged.',
    },
    {
        name: 'Major Updates',
        icon: 'rocket',
        color: '#8b5cf6',
        description: 'The big-beat loop — larger content drops, features and seasons on a longer cadence.',
    },
];

const dayAt = (ms, time) => `${new Date(ms).toISOString().slice(0, 10)}T${time}`;
const atUtc = (ms, hour) => {
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), hour);
};

const seedEvent = (now, id, fields) => ({
    id,
    description: null,
    category: 'LIVEOPS',
    owner: 'GEOPLAY',
    featureId: null,
    scheduleKind: 'RECURRING',
    intervalValue: 1,
    intervalUnit: 'WEEK',
    startLocal: null,
    tzMode: 'FIXED',
    timezone: 'UTC',
    durationSeconds: null,
    recurrence: { end: { type: 'NEVER' } },
    configLinks: [],
    segmentIds: [],
    target: null,
    guardrails: [],
    changes: [],
    rules: [],
    runMode: 'REVIEW',
    reviewLeadHours: null,
    aiSuggested: false,
    sortOrder: 0,
    createdAt: iso(now - 30 * DAY),
    updatedAt: iso(now - 3 * DAY),
    ...fields,
});

const seedItem = (now, id, fields) => ({
    id,
    flywheelId: null,
    laneId: null,
    kind: 'BEAT',
    beatId: null,
    objectiveId: null,
    startAt: iso(now),
    horizonDays: null,
    scheduleOverride: null,
    durationSeconds: null,
    paused: false,
    title: null,
    note: null,
    color: null,
    icon: null,
    sortOrder: 0,
    createdAt: iso(now - 20 * DAY),
    updatedAt: iso(now - 20 * DAY),
    ...fields,
});

const seedTest = (id, name, state, startMs, endMs, fields) => ({
    id,
    name,
    description: null,
    featureId: null,
    featureName: null,
    segmentId: null,
    segmentName: null,
    priority: 0,
    limit: 0,
    rolloutPercentage: 100,
    limitReached: false,
    state,
    variants: [],
    scheduleComponent: {
        schedules: [
            ...(startMs != null ? [{ epochForExecution: startMs, operation: 'START', value: null, percentage: null, index: 0 }] : []),
            ...(endMs != null ? [{ epochForExecution: endMs, operation: 'END', value: null, percentage: null, index: 1 }] : []),
        ],
    },
    ...fields,
});

const seedObjective = (now, id, section, name, current, benchmark, sortOrder) => ({
    id,
    planId: 'gamePlan-1',
    section,
    name,
    metricId: null,
    current,
    benchmark,
    note: null,
    aiSuggested: false,
    sortOrder,
    createdAt: iso(now - 30 * DAY),
    updatedAt: iso(now - 30 * DAY),
});

const seedExtras = (db, now) => {
    if (db.flywheelSeed === SEED_VERSION) return;
    db.flywheelSeed = SEED_VERSION;
    const created = iso(now - 40 * DAY);
    db.flywheels = db.flywheels.map((f) => ({ createdAt: created, updatedAt: created, ...f, lanes: f.lanes || [] }));
    db.placements = db.placements.map((p) => ({ objectiveId: null, durationSeconds: null, paused: false, ...p }));
    db.events.push(
        seedEvent(now, 'liveEvent-login-streak', {
            name: 'Login streak bonus',
            description: 'Extra rewards for logging in on consecutive days.',
            category: 'META',
            intervalUnit: 'DAY',
            startLocal: dayAt(now - 20 * DAY, '00:00'),
            durationSeconds: 86400,
            configLinks: [{ configFileId: 'cfg-progression', parentBranchId: null }],
            sortOrder: 6,
        }),
        seedEvent(now, 'liveEvent-gem-doubler', {
            name: 'Gem pack doubler',
            description: 'Double gems on every pack, every other weekend.',
            category: 'ECONOMY',
            intervalValue: 2,
            startLocal: dayAt(now - 18 * DAY, '18:00'),
            durationSeconds: 172800,
            recurrence: { byWeekday: ['FR'], end: { type: 'NEVER' } },
            configLinks: [{ configFileId: 'cfg-store-offers', parentBranchId: null }],
            sortOrder: 7,
        }),
        seedEvent(now, 'liveEvent-guild-war', {
            name: 'Guild war',
            description: 'Guild against guild over a weekend.',
            category: 'COMPETITIVE',
            scheduleKind: null,
            intervalValue: null,
            intervalUnit: null,
            recurrence: null,
            sortOrder: 8,
        }),
        seedEvent(now, 'liveEvent-tournament', {
            name: 'Weekend tournament',
            description: 'A bracket tournament with ranked rewards.',
            category: 'COMPETITIVE',
            startLocal: dayAt(now + 2 * DAY, '12:00'),
            durationSeconds: 172800,
            sortOrder: 9,
        }),
    );
    db.placements.push(
        seedItem(now, 'timelineItem-gem-doubler', {
            flywheelId: 'flywheel-major', beatId: 'liveEvent-gem-doubler', startAt: iso(now - 18 * DAY), paused: true, sortOrder: 1,
        }),
        seedItem(now, 'timelineItem-season-launch', {
            flywheelId: 'flywheel-major', kind: 'MILESTONE', startAt: iso(atUtc(now + 21 * DAY, 10)), title: 'Season 4 launch',
            icon: 'rocket', color: '--warning', sortOrder: 2,
        }),
        seedItem(now, 'timelineItem-anniversary', {
            flywheelId: 'flywheel-seasonal', kind: 'MILESTONE', startAt: iso(atUtc(now + 48 * DAY, 0)), title: 'Second anniversary',
            icon: 'trophy', color: '--visual-color-3', sortOrder: 2,
        }),
        seedItem(now, 'timelineItem-freeze', {
            flywheelId: 'flywheel-core', kind: 'BLOCK', startAt: iso(atUtc(now + 12 * DAY, 0)), durationSeconds: 4 * 86400,
            title: 'Code freeze', note: 'Client release week. Hold config changes.', color: '--visual-color-2', sortOrder: 3,
        }),
        seedItem(now, 'timelineItem-d7', {
            flywheelId: 'flywheel-core', kind: 'OBJECTIVE', objectiveId: 'gameObjective-d7', startAt: iso(atUtc(now + 3 * DAY, 0)),
            durationSeconds: 14 * 86400, sortOrder: 4,
        }),
    );
    db.objectives = [
        seedObjective(now, 'gameObjective-d7', 'RETENTION', 'D7 retention', '11%', '14%', 0),
        seedObjective(now, 'gameObjective-arpdau', 'MONETIZATION', 'ARPDAU', '$0.21', '$0.25', 1),
        seedObjective(now, 'gameObjective-sessions', 'ENGAGEMENT', 'Sessions per DAU', '3.1', '3.5', 2),
    ];
    db.abTests = [
        seedTest('abTest-starter-price', 'Starter bundle price', 'ACTIVE', now - 6 * DAY, now + 8 * DAY, {
            featureName: 'Store', segmentName: 'New players', rolloutPercentage: 50,
        }),
        seedTest('abTest-xp-curve', 'XP curve tuning', 'ACTIVE', now - 2 * DAY, now + 26 * DAY, {
            featureName: 'Progression', rolloutPercentage: 20,
        }),
        seedTest('abTest-onboarding', 'Onboarding flow v2', 'ACTIVE', now - 20 * DAY, null, { featureName: 'Onboarding' }),
        seedTest('abTest-shop-layout', 'Shop layout', 'PAUSED', now - 10 * DAY, now + 10 * DAY, { featureName: 'Store' }),
        seedTest('abTest-banner', 'Home banner copy', 'DRAFT', null, null, { featureName: 'Home' }),
    ];
};

const flywheelDto = (f) => ({
    id: f.id,
    name: f.name,
    description: f.description ?? null,
    icon: f.icon ?? null,
    color: f.color ?? null,
    sortOrder: f.sortOrder ?? 0,
    seeded: !!f.seeded,
    lanes: f.lanes || [],
    createdAt: f.createdAt ?? null,
    updatedAt: f.updatedAt ?? null,
});

const itemDto = (p) => ({
    id: p.id,
    flywheelId: p.flywheelId,
    laneId: p.laneId ?? null,
    kind: p.kind,
    beatId: p.beatId ?? null,
    objectiveId: p.objectiveId ?? null,
    startAt: p.startAt,
    horizonDays: p.horizonDays ?? null,
    scheduleOverride: p.scheduleOverride ?? null,
    durationSeconds: p.durationSeconds ?? null,
    paused: !!p.paused,
    title: p.title ?? null,
    note: p.note ?? null,
    color: p.color ?? null,
    icon: p.icon ?? null,
    sortOrder: p.sortOrder ?? 0,
    createdAt: p.createdAt ?? null,
    updatedAt: p.updatedAt ?? null,
});

const sortedFlywheels = (db) => [...db.flywheels].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const provision = (db, now) => {
    if (db.flywheels.length) return;
    DEFAULT_FLYWHEELS.forEach((f, i) => db.flywheels.push({
        id: nextId(db, 'flywheel'), ...f, sortOrder: i, seeded: true, lanes: [], createdAt: iso(now), updatedAt: iso(now),
    }));
};

const flywheelOf = (db, id) => {
    const f = db.flywheels.find((x) => x.id === id);
    if (!f) throw notFound('Flywheel');
    return f;
};

const itemOf = (db, id) => {
    const p = db.placements.find((x) => x.id === id);
    if (!p) throw notFound('Timeline item');
    return p;
};

const instanceOf = (db, id) => {
    const inst = db.instances.find((i) => i.id === id);
    if (!inst) throw notFound('Live event instance');
    return inst;
};

const eventFor = (db, item) => db.events.find((e) => e.id === item.beatId) || null;
const has = (data, key) => data?.[key] !== undefined && data?.[key] !== null;

const flywheelFields = (db, data, current) => {
    const out = {};
    if (has(data, 'name') || !current) {
        const name = String(data?.name ?? '').trim();
        if (!name) throw refused('Name is required.');
        if (name.length > NAME_MAX) throw refused(`A name can be at most ${NAME_MAX} characters.`);
        if (db.flywheels.some((f) => f.id !== current?.id && f.name.toLowerCase() === name.toLowerCase())) {
            throw refused(`A flywheel named “${name}” already exists.`);
        }
        out.name = name;
    }
    if (has(data, 'description')) {
        const description = String(data.description).trim();
        if (description.length > DESCRIPTION_MAX) throw refused(`A description can be at most ${DESCRIPTION_MAX} characters.`);
        out.description = description || null;
    }
    if (has(data, 'icon')) out.icon = flywheelIconKey(data.icon);
    if (has(data, 'color')) out.color = cleanColor(data.color);
    if (has(data, 'sortOrder')) out.sortOrder = Math.max(0, Math.floor(Number(data.sortOrder)) || 0);
    return out;
};

const copyName = (db, name) => {
    const taken = (n) => db.flywheels.some((f) => f.name.toLowerCase() === n.toLowerCase());
    let n = `${name} (copy)`.slice(0, NAME_MAX);
    for (let i = 2; taken(n); i += 1) n = `${name} (copy ${i})`.slice(0, NAME_MAX);
    return n;
};

const shipped = (db, itemId) => db.instances.some((i) => i.timelineItemId === itemId && i.publishedAt);

const canon = (v) => {
    if (Array.isArray(v)) return v.map(canon);
    if (v && typeof v === 'object') return Object.fromEntries(Object.keys(v).sort().map((k) => [k, canon(v[k])]));
    return v;
};

const sameJson = (a, b) => JSON.stringify(canon(a)) === JSON.stringify(canon(b));

const clampSeconds = (v) => {
    const n = Math.floor(Number(v));
    return Number.isFinite(n) && n > 0 ? Math.min(MAX_DURATION_S, n) : null;
};

const itemFields = (db, data, current) => {
    const kind = current?.kind || data?.kind;
    if (!KINDS.includes(kind)) throw refused('Invalid item kind');
    const flywheelId = current?.flywheelId || data?.flywheelId;
    flywheelOf(db, flywheelId);
    const out = { kind, flywheelId };
    if (has(data, 'startAt') || !current) {
        const start = Date.parse(data?.startAt);
        if (!Number.isFinite(start)) throw refused('Set a start date');
        out.startAt = iso(start);
    }
    if (kind === 'BEAT' && !current) {
        if (!data?.beatId) throw refused('Pick a Live Event.');
        const event = db.events.find((e) => e.id === data.beatId);
        if (!event) throw notFound('Live Event');
        const ready = readiness(event);
        if (!ready.ready) throw refused(`Not ready to place. ${ready.blockers[0]}`);
        if (db.placements.some((p) => p.flywheelId === flywheelId && p.kind === 'BEAT' && p.beatId === event.id)) {
            throw refused('This event is already on this flywheel. Edit that placement instead of adding a second one.');
        }
        out.beatId = event.id;
    }
    if (kind === 'OBJECTIVE' && !current) {
        const objective = (db.objectives || []).find((o) => o.id === data?.objectiveId);
        if (!objective) throw refused('Pick an objective.');
        out.objectiveId = objective.id;
    }
    if (has(data, 'horizonDays')) out.horizonDays = Math.min(366, Math.max(1, Math.floor(Number(data.horizonDays)) || 90));
    if (kind === 'BEAT' && has(data, 'scheduleOverride')) out.scheduleOverride = cleanOverride(data.scheduleOverride);
    if ((kind === 'BLOCK' || kind === 'OBJECTIVE') && has(data, 'durationSeconds')) out.durationSeconds = clampSeconds(data.durationSeconds);
    if (has(data, 'title')) {
        const title = String(data.title).trim();
        if (title.length > TITLE_MAX) throw refused(`A label can be at most ${TITLE_MAX} characters.`);
        out.title = title || null;
    }
    if (has(data, 'note')) {
        const note = String(data.note).trim();
        if (note.length > NOTE_MAX) throw refused(`A note can be at most ${NOTE_MAX} characters.`);
        out.note = note || null;
    }
    if (has(data, 'color')) out.color = cleanColor(data.color);
    if (has(data, 'icon')) out.icon = data.icon ? flywheelIconKey(data.icon) : null;
    return out;
};

const shipItem = (db, item, now, onlyMs = null) => {
    const event = eventFor(db, item);
    // As on the server, only an event set to publish on its defaults (DEFAULTS) publishes its own dates, and only while
    // it clears that mode's checks; a REVIEW event's dates wait in Upcoming for a person.
    const self = event ? selfPublish(event) : null;
    if (item.kind !== 'BEAT' || item.paused || !self) return { shipped: 0, blocked: null };
    if (self.stalled.length) return { shipped: 0, blocked: self.stalled.join(' ') };
    const blocker = shipBlocker(event, item);
    const len = lengthMs(event, item);
    const dates = onlyMs != null ? [onlyMs].filter((ms) => ms + len > now) : firstDates(event, item, SHIP_AHEAD, now);
    let count = 0;
    dates.forEach((ms) => {
        let inst = instanceAt(db, item.id, ms);
        if (!inst) {
            try {
                inst = openInstance(db, item.id, ms, now);
            } catch {
                return;
            }
        }
        if (inst.publishedAt || inst.skippedAt || inst.cancelledAt || inst.endedEarlyAt) return;
        const plan = planOf(db, inst.id);
        inst.autoRunAt = iso(now);
        inst.updatedAt = iso(now);
        if (plan) {
            if (plan.publishedAt || !plan.withdrawnAt || plan.stagedRevision !== plan.revision) return;
            plan.stagedCopies = plan.stagedCopies.map((c) => ({ ...c, state: 'ACTIVE' }));
            plan.publishedAt = iso(now);
            plan.withdrawnAt = null;
            plan.updatedAt = iso(now);
            inst.publishedAt = plan.publishedAt;
            inst.publishedById = SYSTEM_USER;
            count += 1;
            return;
        }
        if (blocker) {
            inst.autoError = blocker;
            return;
        }
        inst.autoError = null;
        inst.publishedAt = iso(now);
        inst.publishedById = SYSTEM_USER;
        inst.window = { startsAt: iso(ms), endsAt: len ? iso(ms + len) : null };
        count += 1;
    });
    return { shipped: count, blocked: blocker };
};

const windowEndMs = (db, inst) => {
    if (inst.window?.endsAt) return Date.parse(inst.window.endsAt);
    const item = db.placements.find((p) => p.id === inst.timelineItemId);
    const event = item ? eventFor(db, item) : null;
    const len = event ? lengthMs(event, item) : 0;
    return len ? Date.parse(inst.occStart) + len : null;
};

const isRunning = (db, inst, now) => {
    const end = windowEndMs(db, inst);
    return Date.parse(inst.occStart) <= now && (end == null || now < end);
};

const withdrawInstance = (db, inst, now) => {
    if (!inst.publishedAt || inst.endedEarlyAt) return 'NONE';
    if (Date.parse(inst.occStart) <= now) {
        if (!isRunning(db, inst, now)) return 'FINISHED';
        inst.endedEarlyAt = iso(now);
        inst.updatedAt = iso(now);
        return 'ENDED';
    }
    inst.publishedAt = null;
    inst.publishedById = null;
    inst.window = null;
    inst.autoError = null;
    inst.updatedAt = iso(now);
    const plan = planOf(db, inst.id);
    if (plan?.publishedAt) {
        plan.publishedAt = null;
        plan.withdrawnAt = iso(now);
        plan.stagedCopies = plan.stagedCopies.map((c) => ({ ...c, state: 'DRAFT' }));
        plan.updatedAt = iso(now);
    }
    return 'WITHDRAWN';
};

const withdrawItem = (db, item, now) => db.instances
    .filter((i) => i.timelineItemId === item.id)
    .reduce((acc, inst) => {
        const r = withdrawInstance(db, inst, now);
        if (r === 'WITHDRAWN') acc.withdrawn += 1;
        if (r === 'ENDED') acc.ended += 1;
        return acc;
    }, { withdrawn: 0, ended: 0 });

const removeItem = (db, item, now) => {
    const stopped = withdrawItem(db, item, now);
    const ids = new Set(db.instances.filter((i) => i.timelineItemId === item.id).map((i) => i.id));
    db.plans = db.plans.filter((p) => !ids.has(p.instanceId));
    db.instances = db.instances.filter((i) => i.timelineItemId !== item.id);
    db.placements = db.placements.filter((p) => p.id !== item.id);
    return stopped;
};

const summaryOf = (db, limit) => {
    const tests = db.abTests || [];
    return {
        liveCount: tests.filter((t) => t.state === 'ACTIVE').length,
        rollingOutCount: 0,
        draftCount: tests.filter((t) => t.state === 'DRAFT').length,
        recent: tests.slice(0, Math.max(1, Number(limit) || 5)).map((t) => ({
            id: t.id,
            name: t.name,
            state: t.state,
            primaryMetricId: null,
            segmentId: t.segmentId,
            variationCount: (t.variants || []).length,
            rollingOut: false,
            rolloutPercentage: t.rolloutPercentage,
            lastModifiedDate: null,
        })),
    };
};

const upcomingFor = (db, now, limit) => {
    const all = upcomingRows(db, HORIZON_DAYS, now);
    const rows = all.slice(0, limit);
    const days = [];
    rows.forEach((row) => {
        const day = row.occurrenceStart.slice(0, 10);
        const last = days[days.length - 1];
        if (last?.day === day) last.events.push(row);
        else days.push({ day, events: [row] });
    });
    return {
        timezone: 'UTC', horizonDays: HORIZON_DAYS, returnedCount: rows.length, truncated: all.length > rows.length, nextCursor: null, days,
    };
};

const ROUTES = [
    ['get', /^\/flywheels$/, (db, m, req, now) => {
        provision(db, now);
        return { flywheels: sortedFlywheels(db).map(flywheelDto) };
    }],
    ['get', /^\/flywheels\/([^/]+)$/, (db, m) => ({ flywheel: flywheelDto(flywheelOf(db, m[1])) })],
    ['post', /^\/flywheels$/, (db, m, { data }, now) => {
        const fields = flywheelFields(db, data, null);
        const flywheel = {
            id: nextId(db, 'flywheel'), description: null, icon: null, color: null, sortOrder: 0, seeded: false, lanes: [],
            ...fields, createdAt: iso(now), updatedAt: iso(now),
        };
        db.flywheels.push(flywheel);
        return { flywheel: flywheelDto(flywheel) };
    }],
    ['put', /^\/flywheels\/([^/]+)$/, (db, m, { data }, now) => {
        const flywheel = flywheelOf(db, m[1]);
        Object.assign(flywheel, flywheelFields(db, data, flywheel), { updatedAt: iso(now) });
        return { flywheel: flywheelDto(flywheel) };
    }],
    ['delete', /^\/flywheels\/([^/]+)$/, (db, m, req, now) => {
        const flywheel = flywheelOf(db, m[1]);
        const stopped = db.placements.filter((p) => p.flywheelId === flywheel.id)
            .map((p) => removeItem(db, p, now))
            .reduce((acc, s) => ({ withdrawn: acc.withdrawn + s.withdrawn, ended: acc.ended + s.ended }), { withdrawn: 0, ended: 0 });
        db.flywheels = db.flywheels.filter((f) => f.id !== flywheel.id);
        return { success: true, ...stopped };
    }],
    ['post', /^\/flywheels\/([^/]+)\/duplicate$/, (db, m, req, now) => {
        const source = flywheelOf(db, m[1]);
        const copy = {
            id: nextId(db, 'flywheel'),
            name: copyName(db, source.name),
            description: source.description ?? null,
            icon: source.icon ?? null,
            color: source.color ?? null,
            sortOrder: Math.max(-1, ...db.flywheels.map((f) => f.sortOrder ?? 0)) + 1,
            seeded: false,
            lanes: [],
            createdAt: iso(now),
            updatedAt: iso(now),
        };
        db.flywheels.push(copy);
        db.placements.filter((p) => p.flywheelId === source.id).forEach((p) => db.placements.push({
            ...p, id: nextId(db, 'timelineItem'), flywheelId: copy.id, laneId: null, paused: p.kind === 'BEAT' ? true : !!p.paused,
            createdAt: iso(now), updatedAt: iso(now),
        }));
        return { flywheel: flywheelDto(copy) };
    }],
    ['get', /^\/timeline-items$/, (db, m, { params }) => ({
        timelineItems: db.placements
            .filter((p) => !params?.flywheelId || p.flywheelId === params.flywheelId)
            .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt))
            .map(itemDto),
    })],
    ['get', /^\/timeline-items\/([^/]+)$/, (db, m) => ({ timelineItem: itemDto(itemOf(db, m[1])) })],
    ['post', /^\/timeline-items$/, (db, m, { data }, now) => {
        const fields = itemFields(db, data, null);
        const item = {
            ...seedItem(now, nextId(db, 'timelineItem'), fields),
            sortOrder: db.placements.filter((p) => p.flywheelId === fields.flywheelId).length,
            createdAt: iso(now),
            updatedAt: iso(now),
        };
        db.placements.push(item);
        return { timelineItem: itemDto(item), dates: item.kind === 'BEAT' ? shipItem(db, item, now) : null };
    }],
    ['put', /^\/timeline-items\/([^/]+)$/, (db, m, { data }, now) => {
        const item = itemOf(db, m[1]);
        if (item.kind === 'BEAT' && shipped(db, item.id)) {
            const startMoved = has(data, 'startAt')
                && Math.floor(Date.parse(data.startAt) / 60000) !== Math.floor(Date.parse(item.startAt) / 60000);
            const scheduleMoved = has(data, 'scheduleOverride')
                && !sameJson(cleanOverride(data.scheduleOverride), cleanOverride(item.scheduleOverride));
            if (startMoved || scheduleMoved) {
                throw refused('This placement has already shipped, so its start and schedule are locked. Pause or remove it and place the event again to change them.');
            }
        }
        // Pausing takes its published dates back, as the server does; resuming lists them again.
        let stopped = { withdrawn: 0, ended: 0 };
        if (item.kind === 'BEAT' && has(data, 'paused') && !!data.paused !== !!item.paused) {
            item.paused = !!data.paused;
            if (item.paused) stopped = withdrawItem(db, item, now);
            else shipItem(db, item, now);
        }
        Object.assign(item, itemFields(db, data, item), { updatedAt: iso(now) });
        return { timelineItem: itemDto(item), ...stopped };
    }],
    ['delete', /^\/timeline-items\/([^/]+)$/, (db, m, req, now) => {
        return { success: true, ...removeItem(db, itemOf(db, m[1]), now) };
    }],
    ['post', /^\/timeline-items\/([^/]+)\/(pause|resume)$/, (db, m, req, now) => {
        const item = itemOf(db, m[1]);
        if (item.kind !== 'BEAT') throw refused('Only a Live Event placement can be paused.');
        item.paused = m[2] === 'pause';
        item.updatedAt = iso(now);
        const dates = item.paused ? withdrawItem(db, item, now) : shipItem(db, item, now);
        return { timelineItem: itemDto(item), dates };
    }],
    ['get', /^\/live-event-instances$/, (db, m, { params }, now) => {
        const ids = params?.timelineItemId ? new Set([params.timelineItemId])
            : params?.flywheelId ? new Set(db.placements.filter((p) => p.flywheelId === params.flywheelId).map((p) => p.id)) : null;
        return { liveEventInstances: db.instances.filter((i) => !ids || ids.has(i.timelineItemId)).map((i) => instanceDto(i, now)) };
    }],
    ['post', /^\/live-event-instances\/([^/]+)\/withdraw$/, (db, m, req, now) => {
        const inst = instanceOf(db, m[1]);
        const result = withdrawInstance(db, inst, now);
        return { liveEventInstance: instanceDto(inst, now), result };
    }],
    ['post', /^\/live-event-instances\/([^/]+)\/end-early$/, (db, m, req, now) => {
        const inst = instanceOf(db, m[1]);
        if (!inst.publishedAt || inst.endedEarlyAt || !isRunning(db, inst, now)) throw refused('Only a running date can be ended early.');
        inst.endedEarlyAt = iso(now);
        inst.updatedAt = iso(now);
        return { liveEventInstance: instanceDto(inst, now) };
    }],
    ['post', /^\/live-event-instances\/([^/]+)\/ship$/, (db, m, req, now) => {
        const inst = instanceOf(db, m[1]);
        const dates = shipItem(db, itemOf(db, inst.timelineItemId), now, Date.parse(inst.occStart));
        return { liveEventInstance: instanceDto(inst, now), dates };
    }],
    ['get', /^\/live-services\/home$/, (db, m, { params }, now) => {
        provision(db, now);
        const limit = Math.min(50, Math.max(1, Number(params?.upcomingLimit) || 10));
        return { home: { boards: sortedFlywheels(db).map(flywheelDto), upcoming: upcomingFor(db, now, limit) } };
    }],
    ['get', /^\/live-events$/, (db) => ({ liveEvents: [...db.events].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)) })],
    ['get', /^\/game-objectives$/, (db) => ({ gameObjectives: db.objectives || [] })],
    ['get', /^\/ab-test$/, (db) => ({ abTests: db.abTests || [] })],
    ['get', /^\/ab-test\/summary$/, (db, m, { params }) => ({ summary: summaryOf(db, params?.limit) })],
    ['get', /^\/game-config\/files$/, (db) => db.configFiles.map(({ content, ...file }) => file)],
];

export const flywheelMockRequest = async (gameId, { method, path, data, params }) => {
    const route = ROUTES.find(([verb, re]) => verb === method && re.test(path));
    if (!route) return mockRequest(gameId, { method, path, data, params });
    await new Promise((resolve) => { setTimeout(resolve, MOCK_LATENCY_MS); });
    return withMockWorld(gameId, (db, now) => {
        seedExtras(db, now);
        return route[2](db, path.match(route[1]), { data, params }, now);
    });
};

